import { useRef, useState } from "react";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import ContentDetailsModal from "./ContentDetailsModal";
import { useTableState } from "../utils/useTableState";
import api from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import RectangleClassifieds from "../../../creativePreviews/RectangleClassifieds";
import PromotedImagePreview from "../../../creativePreviews/PromotedImagePreview";
import { proxyDownload } from "../../../utils/proxyDownload";
import { roles } from "../../../utils/constants";
import Magnifier from "../../../utils/Magnifier";
import Toggle from "../../../utils/Toggle"
/* ──────────────────────────────────────────────────────────────
   Helper components (FullPreview, ThumbnailPreview, WriteContentForm)
   ────────────────────────────────────────────────────────────── */
const FullPreview = ({ images, currentIndex, setCurrentIndex, isTextEditing, setIsTextEditing, editingText, setEditingText, handleEditText, handleSaveText }) => {
  const current = images[currentIndex];

  const handlePrev = () => setCurrentIndex((p) => (p - 1 + images.length) % images.length);
  const handleNext = () => setCurrentIndex((p) => (p + 1) % images.length);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isZoomed, setIsZoomed] = useState(false);
  const imgRef = useRef(null);

  const handleMouseMove = (e) => {
    const elem = imgRef.current;
    if (!elem) return;

    const { top, left, width, height } = elem.getBoundingClientRect();
    const x = ((e.pageX - left - window.pageXOffset) / width) * 100;
    const y = ((e.pageY - top - window.pageYOffset) / height) * 100;

    setPosition({ x, y });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full h-[80vh] gap-4">
        <Toggle open = {showMagnifier} setOpen={setShowMagnifier} title={"Zoom"}/>
        <div className="relative flex items-center justify-center w-full h-[80%] bg-white rounded-lg shadow-md overflow-hidden">
          <button onClick={handlePrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Previous">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="relative bg-white rounded-lg shadow-lg p-2" onMouseMove={handleMouseMove}>
            <img ref={imgRef} src={current?.url} alt={`creative-${currentIndex}`} className="max-w-[90%] max-h-[90%] object-contain cursor-zoom-in" onClick={() => setIsZoomed(true)} />
          </div>
          {showMagnifier ? <Magnifier showMagnifier={showMagnifier} zoomLevel={5} url={current?.url} position={position} /> : null}
          <button onClick={handleNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Next">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className={`absolute top-2 right-2 w-12 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${current.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>{current.type === "main" ? "MAIN" : "SUB"}</div>
        </div>

        {current.type === "sub" && (
          <div className="text-sm text-black px-2 py-1 bg-yellow-200 rounded">
            {isTextEditing ? (
              <div className="flex items-center gap-2">
                <input type="text" className="px-2 py-1 rounded border" value={editingText.text} onChange={(e) => setEditingText((p) => ({ ...p, text: e.target.value }))} />
                <button className="px-2 py-1 rounded bg-green-500 text-white" onClick={handleSaveText}>
                  Save
                </button>
                <button
                  className="px-2 py-1 rounded bg-gray-300"
                  onClick={() => {
                    setIsTextEditing(false);
                    setEditingText({ url: null, text: "" });
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>Text: {current.text}</span>
                <button
                  className="px-2 py-1 rounded bg-gray-200"
                  onClick={() => {
                    setIsTextEditing(true);
                    handleEditText(current.url, current.text);
                  }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500">
          Image <span className="font-medium">{currentIndex + 1}</span> of {images.length}
        </p>
      </div>

      {isZoomed && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" onClick={() => setIsZoomed(false)}>
          <img src={current.url} alt={`zoomed-${currentIndex}`} className="max-w-full max-h-full object-contain cursor-zoom-out" />
        </div>
      )}
    </>
  );
};

const ThumbnailPreview = ({ images, currentIndex, setCurrentIndex }) => (
  <div className="flex flex-wrap gap-2 my-2 py-1 bg-gray-50 border-t">
    {images.map((img, idx) => (
      <div key={idx} className="relative">
        <img
          src={img.url}
          alt={`thumb-${idx}`}
          onClick={() => setCurrentIndex(idx)}
          className={`w-12 h-12 object-cover rounded cursor-pointer border transition-transform ${idx === currentIndex ? "border-blue-500 scale-105" : "border-gray-300 hover:border-blue-300"}`}
        />
        <div className={`absolute top-0 right-0 px-1 text-xs font-bold text-white rounded-bl ${img.type === "main" ? "bg-green-600" : "bg-orange-600"}`}>{img.type === "main" ? "M" : "S"}</div>
      </div>
    ))}
  </div>
);

const WriteContentForm = ({ selectedForm, variations, handleVariationChange, setSelectedForm, adType }) => {
  const max_headline_limit = adType === "promoted_image_story" ? 75 : 32;
  const max_description_limit = adType === "promoted_image_story" ? 400 : 90;
  const show_headline = adType === "promoted_image_story" || adType === "classifiedads";

  return (
    <div className="flex flex-col gap-4 font-lg max-w-[70vh]">
      <div className="flex gap-2">
        {[1, 2, 3].map((n) => (
          <button key={n} className={`px-3 py-1 rounded ${selectedForm === n ? "bg-blue-600 text-white" : "bg-gray-200"}`} onClick={() => setSelectedForm(n)}>
            Variation {n}
          </button>
        ))}
      </div>

      {show_headline && (
        <div>
          <label className="block text-xs text-gray-700">Headline (Max {max_headline_limit} characters)</label>
          <input
            type="text"
            value={variations[selectedForm - 1].headline}
            onChange={(e) => handleVariationChange(selectedForm, "headline", e.target.value)}
            placeholder="Headline"
            className="w-full border px-2 py-1 rounded text-sm"
            maxLength={max_headline_limit}
          />
          <div className="text-right text-green-800">{max_headline_limit - (variations[selectedForm - 1]?.headline?.trim().length ?? 0)} characters</div>
        </div>
      )}

      <div>
        <label className="block font-medium text-gray-700">Description {show_headline && `(Max ${max_description_limit} characters)`}</label>
        <textarea
          value={variations[selectedForm - 1]?.description}
          onChange={(e) => handleVariationChange(selectedForm, "description", e.target.value)}
          placeholder="Description"
          rows={25}
          className="w-full border max-h-[50vh] px-2 py-1 rounded"
          {...(show_headline ? { maxLength: max_description_limit } : {})}
        />
        {show_headline && <div className="text-right text-green-800">{max_description_limit - (variations[selectedForm - 1]?.description?.trim().length ?? 0)} characters</div>}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   Main component
   ────────────────────────────────────────────────────────────── */
const ContentDetailsCell = ({ creative_id, adDetails}) => {
  /* ────── modal (list of already written content) ────── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentData, setContentData] = useState([]);
  const { fetchContent } = useTableState();

  const closeModal = () => setIsModalOpen(false);
  const handleShowContent = async () => {
    const data = await fetchContent(creative_id);
    setContentData(data);
    setIsModalOpen(true);
  };

  /* ────── drawer (write new content) ────── */
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(1);
  const [variations, setVariations] = useState([
    { id: 1, headline: "", description: "" },
    { id: 2, headline: "", description: "" },
    { id: 3, headline: "", description: "" },
  ]);

  const [adData, setAdData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTextEditing, setIsTextEditing] = useState(false);
  const [editingText, setEditingText] = useState({ url: null, text: "" });
  const [previewMode, setPreviewMode] = useState("full");
  const [isDownloading, setIsDownloading] = useState(false);
  const [content_writer_id, set_content_writer_id] = useState(null);
  const drawerRef = useRef(null);

  /* ────── API helpers ────── */
  const fetchAdData = async (cid) => {
    try {
      const result = await api({
        url: "/user/content_writer/get-ad-details",
        method: "POST",
        data: { creative_id: cid },
      });
      if (result.status === "SUCESS") {
        if (result.data.length === 0) {
          Toast("info", "No Ad Details Found");
        } else {
          set_content_writer_id(result.data[0]?.content_writer_id);
          setAdData(result.data[0]);
          if (result.data[0]?.content?.length) setVariations(result.data[0]?.content);
        }
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Failed to load ad details");
    }
  };

  const handleEditText = (url, txt) => {
    setEditingText({ url, text: txt });
  };

  const handleSaveText = async () => {
    if (!editingText.url || !adData) return;
    try {
      const result = await api({
        url: "/user/content_writer/editText",
        method: "POST",
        data: {
          creative_id: adData.creative_id,
          url: editingText.url,
          text: editingText.text,
        },
      });
      if (result.status === "SUCESS") {
        Toast("success", "Text updated");
        setEditingText({ url: null, text: "" });
        setIsTextEditing(false);
        fetchAdData(creative_id);
      }
    } catch {
      Toast("error", "Failed to update text");
    }
  };

  const hasValidContent = () => {
    const type = adData?.ad_creative_sub_type;
    if (type === "promoted_image_story" || type === "classifiedads") return variations.some((v) => v.headline.trim() && v.description.trim());
    return variations.some((v) => v.description.trim());
  };

  const handleUpload = async () => {
    if (!adData || !hasValidContent()) {
      Toast("error", adData?.ad_creative_sub_type === "promoted_image_story" || adData?.ad_creative_sub_type === "classifiedads" ? "All fields required" : "Description required");
      return;
    }
    if (!adData.content_id) {
      Toast("error", "Cannot Find Content Id\n Please Make sure it is Assigned before proceeding");
      return;
    }
    try {
      const result = await api({
        url: "/user/content_writer/uploadContent",
        method: "POST",
        data: {
          content_id: adData.content_id,
          creative_id: adData.creative_id,
          variations,
        },
      });
      if (result.status === "SUCCESS") {
        Toast("success", "Content uploaded");
        setIsDrawerOpen(false);
        // optional: refresh list
        handleShowContent();
      }else{
         Toast('error',result.data?.message || "Failed to upload content")
      }
    } catch(error) {
      Toast("error", error?.message);
    }
  };

  const handleDownloadImages = async (images, name) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const urls = images.map((i) => i.url);
      await proxyDownload(urls, name);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVariationChange = (id, field, value) => {
    if (field === "form") setSelectedForm(value);
    else setVariations((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setAdData(null);
    setVariations([
      { id: 1, headline: "", description: "" },
      { id: 2, headline: "", description: "" },
      { id: 3, headline: "", description: "" },
    ]);
    setCurrentIndex(0);
    setIsTextEditing(false);
    setEditingText({ url: null, text: "" });
  };

  /* ────── open drawer ────── */
  const openWriter = () => {
    setSelectedForm(1);
    fetchAdData(creative_id);
    setIsDrawerOpen(true);
  };

  /* ────── build image array for preview ────── */
  const buildImages = (data) => {
    const img = data.img_urls || {};
    const images = [];

    if (img.main) images.push({ type: "main", url: img.main, text: null });
    if (Array.isArray(img.sub)) images.push(...img.sub.map((o) => (typeof o === "string" ? { type: "sub", url: o, text: null } : { type: "sub", url: o.url, text: o.text ?? "" })));
    if (img.classified) images.push({ type: "classified", url: img.classified, text: null });
    if (img.thumbnail) images.push({ type: "thumbnail", url: img.thumbnail, text: null });

    return images;
  };
  const role = localStorage.getItem('role');
  return (
    <div className="flex gap-1 flex-col">
      {/* Show already-written content */}
      <button onClick={handleShowContent} className="px-2 py-1 rounded-lg bg-gray-200 border">
        Show Content
      </button>

      
      {
        role && role===roles.ops && <button onClick={openWriter} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors">
          Write Content
        </button>
      }

      {/* Modal with list of existing content */}
      <ContentDetailsModal isOpen={isModalOpen} onClose={closeModal} contentList={contentData} data={adDetails} />

      {/* ────── DRAWER (the whole writer UI) ────── */}
      <SlDrawer ref={drawerRef} label="Ad Preview & Content" style={{ "--size": "100vw" }} placement="end" open={isDrawerOpen} onSlAfterHide={closeDrawer}>
        <div className="flex h-full p-4 gap-4">
          {adData ? (
            (() => {
              const images = buildImages(adData);
              const classifiedImg = adData.img_urls?.classified;
              const isClassified = adData?.ad_type === "classified";

              return (
                <>
                  {/* LEFT – PREVIEW */}
                  <div className="w-2/3 flex flex-col gap-2">
                    {adData.creative_name && <h6>Campaign: {adData.creative_name}</h6>}

                    <button className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? "bg-green-500 cursor-progress" : "bg-blue-400"}`} onClick={() => handleDownloadImages(images, adData.creative_name)} disabled={isDownloading}>
                      Download Images
                    </button>

                    {/* Live / Full toggle (only for certain ad types) */}
                    {(adData.ad_creative_sub_type === "classifiedads" || adData.ad_creative_sub_type === "promoted_image_story") && (
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">Preview</h4>
                        <div className="flex bg-gray-200 rounded-lg p-1">
                          <button className={`px-3 py-1 text-xs rounded-md transition-all ${previewMode === "live" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`} onClick={() => setPreviewMode("live")}>
                            Live Preview
                          </button>
                          <button className={`px-3 py-1 text-xs rounded-md transition-all ${previewMode === "full" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`} onClick={() => setPreviewMode("full")}>
                            Full Images
                          </button>
                        </div>
                      </div>
                    )}

                    {/* PREVIEW AREA */}
                    {previewMode === "live" ? (
                      <div className="flex-1 flex items-center justify-center">
                        {adData?.ad_type === "classified" && (
                          <RectangleClassifieds
                            preview={{
                              img_urls: adData.img_urls?.classified || adData.cla_brand_img,
                              headline: variations[selectedForm - 1].headline || adData.head_line || adData.headline,
                              description: variations[selectedForm - 1].description || adData.description,
                              logo_urls: adData.cla_brand_img,
                              brand_name: adData.cla_brand_name,
                              caption: adData.cla_brand_caption,
                              button_enabled: adData.cla_btn_txt ? 1 : 0,
                              button_text: adData.cla_btn_txt || "",
                              btn_text_bgclr: adData.cla_btn_bg_clr || "#000000",
                              btn_text_clr: adData.cla_btn_txt_col || "#ffffff",
                              name: adData.creative_name,
                            }}
                          />
                        )}
                        {adData.ad_creative_sub_type === "promoted_image_story" && (
                          <PromotedImagePreview
                            preview={{
                              img_urls: adData.img_urls?.main,
                              head_line: variations[selectedForm - 1].headline || adData.head_line,
                              description: variations[selectedForm - 1].description || adData.description,
                              click_url: adData.click_url,
                              button_enabled: adData.button_enabled || 0,
                              button_text: adData.button_text || "",
                              btn_text_bgclr: adData.btn_text_bgclr || "#000000",
                              btn_text_clr: adData.btn_text_clr || "#ffffff",
                              name: adData.creative_name,
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {!isClassified && images.length > 0 && <ThumbnailPreview images={images} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} />}

                        {isClassified ? (
                          classifiedImg ? (
                            <img src={classifiedImg} alt="Classified" className="w-full h-[80%] object-contain rounded-lg" />
                          ) : (
                            <div className="flex items-center justify-center h-[80%] bg-gray-100 rounded-lg">
                              <p className="text-gray-500">No Image</p>
                            </div>
                          )
                        ) : images.length > 0 ? (
                          <FullPreview
                            images={images}
                            currentIndex={currentIndex}
                            setCurrentIndex={setCurrentIndex}
                            isTextEditing={isTextEditing}
                            setIsTextEditing={setIsTextEditing}
                            editingText={editingText}
                            setEditingText={setEditingText}
                            handleEditText={handleEditText}
                            handleSaveText={handleSaveText}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-[80%] bg-gray-100 rounded-lg">
                            <p className="text-gray-500">No Image</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RIGHT – FORM */}
                  <div className="w-1/3 p-4 bg-gray-50 rounded-lg flex flex-col gap-4">
                    <h4 className="font-semibold">Content Writing</h4>
                    {content_writer_id ? (
                      <>
                        <WriteContentForm selectedForm={selectedForm} variations={variations} setSelectedForm={setSelectedForm} handleVariationChange={handleVariationChange} adType={adData?.ad_creative_sub_type} />
                        <button onClick={handleUpload} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                          Upload Content
                        </button>
                      </>
                    ) : (
                      <h6 className="px-2 py-1 shadow-md rounded-md">No Content Writer Assigned</h6>
                    )}
                  </div>
                </>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-full w-full">
              <p className="text-gray-500">Loading…</p>
            </div>
          )}
        </div>
      </SlDrawer>
    </div>
  );
};

export default ContentDetailsCell;
